// components/common/filters/GeneralFilterListSection.tsx
"use client";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import { Checkbox } from "@/app/components/shadcn/checkbox";
import { Label } from "@/app/components/shadcn/label";
import { Input } from "@/app/components/shadcn/input";
import useFilters from "@/app/lib/features/filters/filters.hook";
import {
  GeneralFilterList,
  GeneralFilterColorItem,
  isColorItem,
} from "@/app/lib/features/filters/filters.types";

interface Props {
  filter: GeneralFilterList;
  disableMinimumFilter?: boolean;
}

const isLightColor = (hex: string): boolean => {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return true;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 160;
};

// Not: bu bileşen yalnızca renk ve marka filtrelerini basıyor. "En az bir
// filtre seçili kalmalı" kuralı ikisinde de YOK ve bu bilinçli — kural
// kategori/ürün grubu/alt kategori üçlüsüne özel (bkz. MINIMUM_GUARDED_KEYS).
// Eskiden tüm filtreler tek sayaçta toplandığı için son kalan tek marka da
// kaldırılamıyordu. disableMinimumFilter prop'u çağıranların imzası bozulmasın
// diye duruyor, artık bir şey yapmıyor.
const GeneralFilterListSection = ({ filter }: Props) => {
  const { selected, toggleGeneralList, isGeneralSelected } = useFilters();

  const handleToggleGeneralList = (
    key: "color" | "brand",
    id: string | number,
  ) => {
    toggleGeneralList(key, id);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const key = filter.key as "color" | "brand";
  const selectedValues = (selected[key] ?? []).map(String);
  const selectedCount = selectedValues.length;
  const isColorFilter = filter.items.length > 0 && isColorItem(filter.items[0]);

  const filtered = filter.items.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase()),
  );

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      zIndex: 9999,
    });
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    const handleScroll = () => {
      updatePosition();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const triggerButton = (
    <Button
      ref={triggerRef}
      variant={selectedCount > 0 ? "default" : "outline"}
      size="sm"
      className="h-9 gap-1.5"
      onClick={handleToggle}
    >
      <span>{filter.title}</span>
      {selectedCount > 0 && (
        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-black text-xs font-medium shrink-0">
          {selectedCount}
        </span>
      )}
      <ChevronDown
        size={14}
        className={`transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`}
      />
    </Button>
  );

  const dropdownContent = isColorFilter ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="p-3 bg-white border rounded-md shadow-lg w-64"
    >
      {filter.search && (
        <Input
          placeholder={`${filter.title} Ara`}
          className="mb-3 h-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="max-h-52 overflow-y-auto">
        <div className="grid grid-cols-4 gap-3 px-1 py-1.5">
          {(filtered as GeneralFilterColorItem[]).map((item, idx) => {
            const isChecked = isGeneralSelected(key, item.id);
            const hex = item.hex.trim();
            return (
              <div
                key={`${item.id}-${idx}`}
                className="flex flex-col items-center gap-1"
              >
                <button
                  type="button"
                  onClick={() => handleToggleGeneralList(key, item.id)}
                  className="relative w-6 h-6 rounded-full cursor-pointer flex items-center justify-center transition-all ring-1 ring-gray-300"
                  style={{
                    backgroundColor: hex,
                    outline: isChecked ? "2px solid #111827" : "none",
                    outlineOffset: "2px",
                    transform: isChecked ? "scale(1.1)" : "scale(1)",
                  }}
                >
                  {isChecked && (
                    <Check
                      size={10}
                      className="drop-shadow"
                      style={{
                        color: isLightColor(hex) ? "#111827" : "#ffffff",
                      }}
                    />
                  )}
                </button>
                <span className="text-[10px] text-muted-foreground text-center leading-tight truncate w-full">
                  {item.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ) : (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="p-3 bg-white border rounded-md shadow-lg w-56"
    >
      {filter.search && (
        <Input
          placeholder={`${filter.title} Ara`}
          className="mb-3 h-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="max-h-52 overflow-y-auto space-y-1.5">
        {filtered.map((item, idx) => {
          const itemId = `gen-${filter.key}-${item.id}-${idx}`;
          const isChecked = isGeneralSelected(key, item.id);
          return (
            <div key={itemId} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={itemId}
                  checked={isChecked}
                  onCheckedChange={() => handleToggleGeneralList(key, item.id)}
                />
                <Label
                  htmlFor={itemId}
                  className="text-[13px] text-muted-foreground font-normal cursor-pointer"
                >
                  {item.name}
                </Label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {triggerButton}
      {isOpen &&
        typeof window !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default GeneralFilterListSection;
